/**
 * Assessment Components that a Student can perform activity within.
 *
 * An instance of a Component is unique in the Aphrodite client for a student.
 */
export type Component =
  | AssessmentComp
  | WorkComp
  | CharterComp
  | PeerComp
  | AnswerComp
  | FeedbackComp;

/**
 * Top-level Assessment component.
 */
export interface AssessmentComp {
  type: "assessment";
}

/** A Work component parameterised by a specific Work ID. */
export interface WorkComp {
  type: "work";
  workId: string;
}

/** Answer component for a specific answer. */
export interface AnswerComp {
  type: "answer";
  answerBlockId: string;
}

/** The group Charter component. */
export interface CharterComp {
  type: "charter";
}

/** Peer evaluation component. */
export interface PeerComp {
  type: "peer";
}

/** The final Feedback component. */
export interface FeedbackComp {
  type: "feedback";
}

/**
 * A ActivitySpan describes a span of activity that a Student is conducting
 * within their current Session.
 *
 * AcitivitySpans with the same `spanId` have occured close to each other within
 * an `IDLE_BUFFER` amount of time, and are the same kind of *continuous*
 * activity for the same `component`.
 */
export interface ActivitySpan {
  spanId: string;
  component: Component;
  iat: number; // Issued At Time
}

// [private] Singleton cache of all active Spans for different components.
const SPAN_CACHE: Record<string, ActivitySpan> = {};
const IDLE_BUFFER = 5 * 60 * 1000; // 5 minutes in milliseconds

// [private] Current active ActivitySpan. A client instance will only conduct
// one kind of activity at a time.
let CURRENT_SPAN: ActivitySpan | null = null;

/**
 * Returns the current ActivitySpan that the client is conducting.
 *
 * The Activity time is bumped if the current ActivitySpan is still active.
 * Otherwise, the Activity is renewed in a new span.
 */
export function currentActivitySpan(): ActivitySpan | null {
  if (CURRENT_SPAN) {
    return startSpan(CURRENT_SPAN.component);
  }
  return null;
}

/**
 * Starts a new ActivitySpan indicating the Student is switching to working
 * under the `component`.
 */
export function startSpan(component: Component): ActivitySpan {
  const now = Date.now();
  const key = encodeComponent(component);
  const existing = SPAN_CACHE[key];

  if (existing && !isExpiredSpan(existing, now)) {
    const span = extendSpan(existing, now);
    SPAN_CACHE[key] = span;
    CURRENT_SPAN = span;
    return span;
  }

  const span = newSpan(component, now);
  SPAN_CACHE[key] = span;
  CURRENT_SPAN = span;
  return span;
}

function extendSpan(span: ActivitySpan, now: number) {
  return { ...span, iat: now };
}

function isExpiredSpan(span: ActivitySpan, now: number) {
  const expiry = span.iat + IDLE_BUFFER;
  return now > expiry;
}

function newSpan(type: Component, now: number): ActivitySpan {
  return {
    component: type,
    spanId: genSpanId(type),
    iat: now,
  };
}

/**
 * Encode the given ActivitySpan into a string suitable for use as a well-known
 * `x-cadmus-span` header for backend systems and service to understand.
 */
export function encodeSpanHeader(span: ActivitySpan): string {
  return span.spanId;
}

// Unique key ID for a SpanType
function encodeComponent(type: Component): string {
  switch (type.type) {
    case "assessment":
      return "assessment";
    case "work":
      return `work:${type.workId}`;
    case "charter":
      return "charter";
    case "peer":
      return "peer";
    case "answer":
      return `answer:${type.answerBlockId}`;
    case "feedback":
      return "feedback";
  }
}

/** Time-based sortable span ID. */
function genSpanId(component: Component): string {
  const randId = Math.floor(Math.random() * 100).toString();
  const timeId = new Date().getTime();
  const key = encodeComponent(component);
  return `span:${key}:${timeId}${randId}`;
}
